<template>
    <div>
        <Card>
            <template #title>
                Contas
            </template>
            <template #content>
                <Toolbar class="mb-2 bg-gray-100">
                    <template #start>
                        <div class="flex items-center gap-1">
                            <SplitButton label="Exportação" icon="pi pi-file-excel" severity="success" size="small"
                                :model="exportSplitModel" @click="exportarData" />
                            <SplitButton v-if="permissoes.CONTAS_INCLUIR" label="Upload" icon="pi pi-upload"
                                severity="help" size="small" :model="uploadSplitModel" @click="openCarneForm()" />
                            <span v-if="permissoes.ARQUIVO_DOWNLOAD_LOTE" class="inline-flex items-center gap-1">
                                <Button label="Baixar zip unitário" icon="pi pi-download" severity="info" size="small"
                                    :disabled="!selectedRows.length" @click="openLoteDialog" />
                                <i v-if="!selectedRows.length"
                                    class="pi pi-question-circle text-xs text-color-secondary opacity-60 cursor-help"
                                    v-tooltip.top="'Você precisa selecionar alguma conta para baixar o seu zip'"
                                    aria-label="Ajuda: baixar zip unitário" />
                            </span>
                        </div>
                    </template>
                    <template #end>
                        <div class="flex items-center gap-1">
                            <Button v-if="permissoes.CONTAS_INCLUIR" icon="pi pi-plus" label="Incluir" severity="info"
                                size="small" @click="openForm({})" />
                            <Button icon="pi pi-search" label="Buscar" severity="success" size="small"
                                @click="toggleBusca" />
                            <Button icon="pi pi-refresh" label="Atualizar" severity="warn" size="small"
                                @click="loadData" />
                        </div>
                    </template>
                </Toolbar>

                <DataTable :value="lista" :loading="isLoading" size="small" stripedRows showGridlines scrollable
                    scrollHeight="calc(100vh - 300px)" :sortField="params.sort.field" :sortOrder="params.sort.order"
                    v-model:selection="selectedRows"
                    @sort="onTableSort">

                    <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
                    <Column style="width: 100px" field="id" header="#" sortable></Column>
                    <Column field="id_primario" header="ID Primário" sortable></Column>
                    <Column field="id_secundario" header="ID Secundário" sortable></Column>
                    <Column field="valor" header="Valor" sortable>
                        <template #body="{ data }">
                            R$ {{ formatMoney(data.valor) }}
                        </template>
                    </Column>
                    <Column field="data_mam" header="Data MAM" sortable>
                        <template #body="{ data }">
                            {{ dateFormat(data.data_mam) }}
                            <template v-if="data.data_mam_dias > 0">
                                ({{data.data_mam_dias}} dias)
                            </template>
                        </template>
                    </Column>
                    <Column field="data_vencimento" header="Vencimento" sortable>
                        <template #body="{ data }">
                            {{ dateFormat(data.data_vencimento) }}
                            <template v-if="data.data_vencimento_dias > 0">
                                ({{data.data_vencimento_dias}} dias)
                            </template>
                            <template v-if="semBoletoUrgente(data)">
                                <Badge value="! sem boleto"
                                    :severity="data.data_vencimento_dias <= 7 ? 'danger' : 'warn'"
                                    v-tooltip.top="`Vence em ${data.data_vencimento_dias} dia(s) sem boleto cadastrado`"
                                    class="ml-1" />
                            </template>
                        </template>
                    </Column>
                    <Column field="cliente" header="Cliente" sortable></Column>
                    <Column field="provedor" header="Provedor" sortable></Column>
                    <Column field="site" header="Site/Localidade" sortable></Column>


                    <Column style="width: 180px"
                        bodyClass="flex justify-content-end gap-1">

                        <template #body="{ data }">
                            <Badge :value="data.boleto_id ? 'CB' : 'SB'" :severity="data.boleto_id ? 'success' : 'danger'" v-tooltip.top="data.boleto_id ? 'Com Boleto' : 'Sem Boleto'" >
                            </Badge>
                            <Badge :value="data.comprovante_id ? 'CC' : 'SC'" :severity="data.comprovante_id ? 'success' : 'danger'" v-tooltip.top="data.comprovante_id ? 'Com Comprovante' : 'Sem Comprovante'" >
                            </Badge>
                            <Badge :value="data.data_pagamento ? '$' : '$'" :severity="data.data_pagamento ? 'success' : 'danger'" v-tooltip.top="data.data_pagamento ? 'Pago' : 'Pendente'" >
                            </Badge>
                            <Button v-if="permissoes.CONTAS_EDITAR" icon="pi pi-pen-to-square" title="Atualizar" class="p-button-rounded p-button-text p-button-info p-0 w-2rem h-2rem"
                                @click="openForm(data)" />
                            <Button v-if="permissoes.CONTAS_REMOVER" icon="pi pi-trash" title="Remover" class="p-button-rounded p-button-text p-button-danger p-0 w-2rem h-2rem"
                                @click="remove(data)" />
                        </template>
                    </Column>

                    <template #empty>
                        <div class="text-center text-gray-600">
                            Nenhum registro encontrado
                        </div>
                    </template>
                </DataTable>

                <div class="relative flex align-items-center justify-content-center mt-2 py-1">
                    <span class="absolute left-0 text-sm text-gray-600">{{ pagination.total }} registros</span>
                    <Paginator :first="pagination.first" :rows="pagination.perPage" :totalRecords="pagination.total"
                        @page="onPage" />
                </div>

            </template>
        </Card>

        <Popover ref="busca" :closeOnEscape="false" :dismissable="false" class="w-4">
            <div class="w-full">
                <form @submit.prevent="onSubmitBusca">
                    <div class="w-full">
                        <div class="field mt-4">
                            <FloatLabel>
                                <InputText id="contrato" v-model="params.filter.contrato" class="w-full" />
                                <label for="contrato">ID Primário e Secundário </label>
                            </FloatLabel>
                        </div>

                        <div class="list-none p-0 m-0 flex flex-col gap-2">
                            <div class="field mt-1 w-full">
                                <FloatLabel>
                                    <AutoComplete 
                                        id="cliente"
                                        v-model="selectedClientes" 
                                        :suggestions="clienteSuggestions"
                                        @complete="searchClientes"
                                        @item-select="onClienteSelect"
                                        optionLabel="nome"
                                        :multiple="true"
                                        class="w-full"
                                        inputClass="w-full"
                                    >
                                        <template #chip="{ value }">
                                            <div 
                                                class="inline-flex items-center gap-2 px-2 py-1 mr-2 my-1 text-sm rounded border align-middle"
                                                :class="value.exclude ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'"
                                            >
                                                <i 
                                                    :class="[
                                                        value.exclude ? 'pi pi-ban' : 'pi pi-check-circle',
                                                        'cursor-pointer hover:scale-110 transition-transform flex-shrink-0'
                                                    ]"
                                                    @click.stop="toggleClienteExclude(value)"
                                                    title="Alternar Incluir/Excluir"
                                                ></i>
                                                
                                                <span 
                                                    class="font-medium select-none block" 
                                                    style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                                                    :title="value.nome"
                                                >
                                                    {{ value.nome }}
                                                </span>
                                                
                                                <div 
                                                    class="w-px h-3 mx-1 opacity-40 flex-shrink-0"
                                                    :class="value.exclude ? 'bg-red-700' : 'bg-blue-700'"
                                                ></div>

                                                <i 
                                                    class="pi pi-times cursor-pointer hover:font-bold hover:bg-black/5 rounded-full p-0.5 transition-colors flex-shrink-0"
                                                    @click.stop="removeCliente(value)"
                                                    title="Remover"
                                                ></i>
                                            </div>
                                        </template>
                                    </AutoComplete>
                                    <label for="cliente">Cliente</label>
                                </FloatLabel>
                            </div>
                            <div class="field mt-1 w-full">
                                <FloatLabel>
                                    <InputText id="provedor" v-model="params.filter['pro.nome']" class="w-full" />
                                    <label for="provedor">Provedor</label>
                                </FloatLabel>
                            </div>
                        </div>
                        <div class="list-none p-0 m-0 flex flex-col gap-1">
                            <div class="field mt-1 w-full">
                                <FloatLabel>
                                    <InputText id="site" v-model="params.filter['con.site']" class="w-full" />
                                    <label for="site">Site/Localidade</label>
                                </FloatLabel>
                            </div>

                            <div class="field mt-1 w-full">
                                <FloatLabel>
                                    <Select v-model="params.filter.pago"
                                            :options="[
                                                { id: 1, boleto: 'Pago' },
                                                { id: 2, boleto: 'Pendente' }
                                            ]"
                                            optionLabel="boleto"
                                            optionValue="id"
                                            showClear
                                            class="w-full"
                                            />
                                    <label>Situação Pagamento</label>
                                </FloatLabel>
                            </div>

                        </div>


                        <div class="list-none p-0 m-0 flex flex-col gap-1">

                            <div class="field mt-1  w-full">
                                <FloatLabel>
                                    <Select v-model="params.filter.boleto"
                                            :options="[
                                                { id: 1, boleto: 'Com Boleto' },
                                                { id: 2, boleto: 'Sem Boleto' }
                                            ]"
                                            optionLabel="boleto"
                                            optionValue="id"
                                            showClear
                                            class="w-full"
                                            />
                                    <label>Situação Boleto</label>
                                </FloatLabel>
                            </div>

                            <div class="field mt-1  w-full">
                                <FloatLabel>
                                    <Select v-model="params.filter.comprovante"
                                            :options="[
                                                { id: 1, comprovante: 'Com Comprovante' },
                                                { id: 2, comprovante: 'Sem Comprovante' }
                                            ]"
                                            optionLabel="comprovante"
                                            optionValue="id"
                                            showClear
                                            class="w-full"
                                            />
                                    <label>Situação Comprovante</label>
                                </FloatLabel>
                            </div>
                        </div>

                        <div class="list-none p-0 m-0 flex flex-col gap-1">
                            <div class="field mt-1 w-6">
                                <FloatLabel>
                                    <DatePicker id="data_vencimento_inicio" v-model="params.filter.data_vencimento_inicio"
                                        showIcon class="w-full" dateFormat="dd/mm/yy" />
                                    <label for="data_vencimento_inicio">Vencimento Inicio</label>
                                </FloatLabel>
                            </div>
                            <div class="field mt-1 w-6">
                                <FloatLabel>
                                    <DatePicker id="data_vencimento_fim" v-model="params.filter.data_vencimento_fim" showIcon
                                        class="w-full" dateFormat="dd/mm/yy" />
                                    <label for="data_vencimento_fim">Vencimento Fim</label>
                                </FloatLabel>
                            </div>
                        </div>


                        <div class="list-none p-0 m-0 flex flex-col gap-1">
                            <div class="field mt-1 w-6">
                                <FloatLabel>
                                    <DatePicker id="data_mam_inicio" v-model="params.filter.data_mam_inicio"
                                        showIcon class="w-full" dateFormat="dd/mm/yy" />
                                    <label for="data_mam_inicio">MAM Inicio</label>
                                </FloatLabel>
                            </div>
                            <div class="field mt-1 w-6">
                                <FloatLabel>
                                    <DatePicker id="data_mam_fim" v-model="params.filter.data_mam_fim" showIcon
                                        class="w-full" dateFormat="dd/mm/yy" />
                                    <label for="data_mam_fim">MAM Fim</label>
                                </FloatLabel>
                            </div>
                        </div>
                        <div class="field mt-1">
                            <FloatLabel>
                                <Select v-model="params.filter.data_vencimento_variavel_inicio"
                                        :options="[
                                            { id: 5, vencimentos: '5 dias' },
                                            { id: 10, vencimentos: '10 dias' },
                                            { id: 15, vencimentos: '15 dias' },
                                            { id: 20, vencimentos: '20 dias' }
                                        ]"
                                        optionLabel="vencimentos"
                                        optionValue="id"
                                        showClear
                                        class="w-full" />
                                <label>Vencimentos</label>
                            </FloatLabel>
                        </div>

                        <div class="field mt-4">
                            <FloatLabel>
                                <InputText id="codigo_de_barras" v-model="params.filter.codigo_de_barras" class="w-full" />
                                <label for="codigo_de_barras">Código de Barras</label>
                            </FloatLabel>
                        </div>
                    </div>

                    <div class="flex justify-content-end gap-1">
                        <Button type="submit" label="Buscar" severity="success" icon="pi pi-search"
                            class="flex align-items-center justify-content-center" />
                        <Button icon="pi pi-eraser" label="Limpar" severity="warn" size="small"
                            class="flex align-items-center justify-content-center" @click="onClearBusca" />
                        <Button icon="pi pi-times" label="Fechar" severity="danger" size="small"
                            class="flex align-items-center justify-content-center" @click="toggleBusca" />
                    </div>
                </form>
            </div>
        </Popover>

        <ContaForm
            :visible="visibleForm"
            :data="item"
            :initial-tab-index="contaFormInitialTab"
            @close="closeForm"
            @save="loadData"
        />
        <CarneForm :visible="visibleCarneForm" :data="item" @close="closeForm" @save="loadData" />
        <UploadIAForm
            :visible="visibleUploadIAForm"
            :permissoes="permissoes"
            @close="onUploadIAClose"
            @success="onIASuccess"
            @duplicate-contas="onIADuplicateContas"
        />

        <ContaListaDialog
            :visible="visibleContaDuplicateDialog"
            :error="contaDuplicateError"
            :contas="contaDuplicateIds"
            @close="onContaDuplicateDialogClose"
        />
        <ArquivoLoteDialog :visible="visibleLoteDialog" origem="contas"
            :selected-ids="selectedRows.map(row => row.id)" @close="visibleLoteDialog = false" />
    </div>
</template>
<script>
import Card from 'primevue/card';

/* Table */
import Toolbar from 'primevue/toolbar';
import Button from 'primevue/button';
import SplitButton from 'primevue/splitbutton';
import Badge from 'primevue/badge';
import DataTable from 'primevue/datatable';
import Paginator from 'primevue/paginator';
import Column from 'primevue/column';
import Select from 'primevue/select';

import Popover from 'primevue/popover';
import FloatLabel from 'primevue/floatlabel';
import InputText from 'primevue/inputtext';
import DatePicker from 'primevue/datepicker';
import AutoComplete from 'primevue/autocomplete';
import Checkbox from 'primevue/checkbox';

import Conta from '@/service/conta.js';
import Cliente from '@/service/cliente.js';

import ContaForm from '@/page/conta/Form.vue';
import CarneForm from '@/page/conta/CarneForm.vue';
import ContratoForm from '@/page/contrato/Form.vue';
import UploadIAForm from '@/page/conta/UploadIAForm.vue';
import ContaListaDialog from '@/page/conta/ListaDialog.vue';
import ArquivoLoteDialog from '@/page/arquivo/LoteDialog.vue';


import Filter from '@/utils/Filter.js';
import File from '@/utils/File.js';
import Mask from '@/utils/Mask.js';

import Moment from 'moment';

export default {
    components: {
        Card,
        Toolbar,
        Badge,
        Button,
        SplitButton,
        DataTable,
        Paginator,
        Column,
        DatePicker,
        Select,

        /* Busca */
        Popover,
        FloatLabel,
        InputText,
        AutoComplete,
        Checkbox,

        /* Form */
        ContaForm,
        ContratoForm,
        CarneForm,
        UploadIAForm,
        ContaListaDialog,
        ArquivoLoteDialog,
    },
    data() {
        return {
            permissoes: permissoes,
            lista: [],
            isLoading: false,
            errors: {},
            item: {},
            visibleForm: false,
            visibleCarneForm: false,
            visibleUploadIAForm: false,
            /** Após Upload IA com persistência automática, abre direto na aba Arquivos. */
            contaFormInitialTab: 0,

            visibleContaDuplicateDialog: false,
            contaDuplicateError: null,
            contaDuplicateIds: [],

            selectedRows: [],
            visibleLoteDialog: false,

            // AutoComplete clientes
            selectedClientes: [],
            clienteSuggestions: [],

            params: {
                filter: {},
                sort: {
                    field: 'id',
                    order: 1
                }
            },
            pagination: {
                page: 0,
                perPage: 0,
                total: 0,
            },
        }
    },
    computed: {
        uploadSplitModel() {
            return [
                {
                    label: 'Upload IA (Beta)',
                    icon: 'pi pi-sparkles',
                    command: () => this.openUploadIAForm(),
                },
            ];
        },
        exportSplitModel() {
            const items = [];

            if (this.permissoes.EXPORTACAO_CONSOLIDADA) {
                items.push({
                    label: 'Exportação completa',
                    icon: 'pi pi-file-export',
                    command: () => this.goExportacaoConsolidada('pagamentos'),
                });
            }

            return items;
        },
    },
    beforeMount() {
        this.pagination.page = parseInt(this.$route.query.page || 0);
        document.addEventListener("onPermissios", event => this.permissoes = event.permissoes);
    },
    mounted() {
        this.loadData();
    },
    methods: {

        openUploadIAForm() {
            this.visibleUploadIAForm = true;
        },
        openLoteDialog() {
            if (!this.selectedRows.length) {
                return;
            }
            this.visibleLoteDialog = true;
        },

        /**
         * Mesmo fluxo do upload carnê ({@see CarneForm}): toast "Ops!" no ListaDialog + tabela de contas com o código já cadastrado.
         */
        onIADuplicateContas({ msg, contas }) {
            this.contaDuplicateError = msg;
            this.contaDuplicateIds = Array.isArray(contas) ? contas : [];
            this.visibleContaDuplicateDialog = true;
        },

        onContaDuplicateDialogClose() {
            this.visibleContaDuplicateDialog = false;
            this.contaDuplicateError = null;
            this.contaDuplicateIds = [];
        },

        async onIASuccess(data) {
            this.visibleUploadIAForm = false;

            try {
                const payload = this.buildContaCreatePayloadFromIA(data);
                const response = await Conta.create(payload);
                const criada = response?.data?.data;
                if (!criada || criada.id == null) {
                    throw new Error(
                        'Resposta inválida ao criar conta (sessão ou rota). Verifique se o POST /api/conta retornou JSON com data.id.'
                    );
                }
                this.item = Conta.parse(criada);
                this.contaFormInitialTab = 1;
                this.visibleForm = true;
                /**
                 * Lista inicia com ordenação id ASC: registros novos (maior id) vão para a última página —
                 * parece que “não salvou”. Após criar pela IA, ordenamos id DESC e voltamos à primeira página.
                 */
                this.pagination.page = 0;
                this.params.sort = { field: 'id', order: -1 };
                if (this.$route?.name) {
                    this.$router.replace({ name: this.$route.name, query: { ...this.$route.query, page: 0 } }).catch(() => {});
                }
                this.loadData();
                this.$toast.add({
                    severity: 'success',
                    summary: 'Conta salva',
                    detail: 'Os dados extraídos foram gravados. Use a aba Arquivos para enviar o PDF do boleto.',
                    life: 6000,
                });
            } catch (error) {
                const merged = {
                    ...data,
                    mensalidade: 0,
                    prorata: 0,
                    tipo_conta: 'avulso',
                };
                this.item = Conta.parse(merged);
                this.item.tipo_conta = 'avulso';
                this.contaFormInitialTab = 0;
                this.visibleForm = true;

                const apiMsg = error.response?.data?.msg;
                if (error.response?.data?.erros) {
                    const erros = error.response.data.erros;
                    const primeiroCampo = Object.keys(erros)[0];
                    const primeiraLinha = primeiroCampo
                        ? Array.isArray(erros[primeiroCampo])
                            ? erros[primeiroCampo][0]
                            : erros[primeiroCampo]
                        : null;
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Não foi possível salvar automaticamente',
                        detail: primeiraLinha || apiMsg || 'Revise os dados na aba Informações e clique em Salvar.',
                        life: 9000,
                    });
                    return;
                }
                const fallbackMsg = apiMsg || error.message;
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro ao salvar conta',
                    detail: fallbackMsg,
                    life: 8000,
                });
            }
        },

        /**
         * Monta o POST /api/conta compatível com {@see ContaCtrl::create} a partir do retorno do Upload IA.
         */
        buildContaCreatePayloadFromIA(data) {
            const contratoId = parseInt(data.contrato_id, 10);
            if (!contratoId) {
                throw new Error('Contrato obrigatório');
            }

            const valorBruto = this.parseValorMonetario(data.valor);
            if (Number.isNaN(valorBruto)) {
                throw new Error('Valor inválido');
            }
            // API valida com decimal:0,2 — float do parser pode ter muitas casas e o POST falha sem gravar.
            const valor = Math.round(valorBruto * 100) / 100;

            const dataVencimento = this.formatDateForApi(data.data_vencimento ?? data.vencimento);
            if (!dataVencimento) {
                throw new Error('Data de vencimento obrigatória');
            }

            const payload = {
                contrato_id: contratoId,
                mensalidade: 0,
                prorata: 0,
                valor,
                data_vencimento: dataVencimento,
                // tipo_cobranca é obrigatório na API — default sensato; o usuário
                // revisa no formulário que abre após a criação pela IA.
                tipo_cobranca: 'mensalidade',
                observacao: data.observacao || null,
            };
            if (data.codigo_de_barras) {
                payload.codigo_de_barras = String(data.codigo_de_barras).replace(/\D/g, '');
            }
            // J-52: beneficiário extraído pela IA (CNPJ validado no servidor).
            if (data.beneficiario_cnpj) {
                payload.beneficiario_cnpj = String(data.beneficiario_cnpj).replace(/\D/g, '');
            }
            if (data.beneficiario) {
                payload.beneficiario_nome = data.beneficiario;
            }
            return payload;
        },

        formatDateForApi(val) {
            if (val === null || val === undefined || val === '') {
                return null;
            }
            if (val instanceof Date) {
                const y = val.getFullYear();
                const m = String(val.getMonth() + 1).padStart(2, '0');
                const d = String(val.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
            }
            if (typeof val === 'string') {
                const iso = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (iso) {
                    return `${iso[1]}-${iso[2]}-${iso[3]}`;
                }
                const br = val.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
                if (br) {
                    return `${br[3]}-${br[2]}-${br[1]}`;
                }
            }
            return null;
        },

        parseValorMonetario(val) {
            if (val === null || val === undefined || val === '') {
                return NaN;
            }
            if (typeof val === 'number') {
                return val;
            }
            const normalized = String(val)
                .trim()
                .replace(/\s/g, '')
                .replace(/^R\$/i, '')
                .replace(/[^\d,.\-]/g, '');
            if (!normalized) {
                return NaN;
            }
            if (normalized.includes(',')) {
                return parseFloat(normalized.replace(/\./g, '').replace(',', '.'));
            }
            return parseFloat(normalized);
        },

        /** Fecha só o modal de IA sem limpar `item` (evita apagar dados antes do formulário de conta). */
        onUploadIAClose() {
            this.visibleUploadIAForm = false;
        },

        openForm(item) {
            this.item = { ...item };
            this.visibleForm = true;
        },
        openCarneForm(item) {
            this.item = { ...item };
            this.visibleCarneForm = true;
        },
        closeForm(event) {
            this.item = {};
            this.contaFormInitialTab = 0;
            this.visibleForm = false;
            this.visibleCarneForm = false;
            this.visibleUploadIAForm = false;
        },
        remove(data) {
            this.$confirm.require({
                message: 'Deseja realmente remover o item?',
                header: 'Remover',
                acceptLabel: 'Remover',
                icon: 'pi pi-trash',
                acceptClass: 'bg-red-500 text-white border-red-500',
                rejectLabel: 'Cancelar',
                rejectClass: 'bg-red-400 text-white',
                accept: () => {
                    this.isLoading = true;
                    Conta.delete(data.id)
                        .then(response => {
                            this.$toast.add({ severity: 'success', summary: 'Sucesso', detail: 'O item foi removido com sucesso.', life: 3000 });
                            this.loadData();
                        })
                        .catch(error => {
                            if (error.response) {
                                let err = error.response.data;
                                if (err.erros) {
                                    for (let i in err.erros) {
                                        this.errors[i] = err.erros[i][0];
                                    }
                                    return;
                                }
                                if (error.response.data.msg) {
                                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                                    return;
                                }
                            }
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                        })
                        .finally(() => {
                            this.isLoading = false;
                        });
                },
            });
        },
        // AutoComplete clientes
        searchClientes(event) {
            const query = event.query;
            Cliente.search(query)
                .then(response => {
                    // Filtra itens que já estão selecionados
                    const selectedIds = this.selectedClientes.map(c => c.id);
                    this.clienteSuggestions = response.data.filter(c => !selectedIds.includes(c.id));
                })
                .catch(error => {
                    console.error('Erro ao buscar clientes:', error);
                    this.clienteSuggestions = [];
                });
        },
        onClienteSelect(event) {
            // Define padrão como 'incluir' (exclude = false)
            // Precisamos garantir reatividade ao adicionar a propriedade
            if(event.value && typeof event.value.exclude === 'undefined'){
                event.value.exclude = false;
            }
        },
        toggleClienteExclude(cliente) {
            // Alterna entre incluir e excluir
            cliente.exclude = !cliente.exclude;
        },
        removeCliente(cliente) {
            this.selectedClientes = this.selectedClientes.filter(c => c.id !== cliente.id);
        },
        loadDataParams() {
            let params = [];

            // Pagination
            params.push(`page=${1 + this.pagination.page}`);

            // Filter
            params = Filter.formToQuery(this.params.filter, params);

            // Cliente filtro (AutoComplete Múltiplo)
            if (this.selectedClientes && this.selectedClientes.length > 0) {
                const incluir = [];
                const excluir = [];

                this.selectedClientes.forEach(c => {
                    if (c.exclude) {
                        excluir.push(c.id);
                    } else {
                        incluir.push(c.id);
                    }
                });

                if (incluir.length > 0) {
                    params.push(`filter[cliente_id]=${incluir.join(',')}`);
                }
                
                if (excluir.length > 0) {
                    params.push(`filter[exclude_cliente_id]=${excluir.join(',')}`);
                }
            }

            // Order
            if(['id'].indexOf(this.params.sort.field)>= 0){
                params.push(`order[cont.${this.params.sort.field}]=` + (this.params.sort.order > 0 ? 'asc' : 'desc'));

            }
            else{
                params.push(`order[${this.params.sort.field}]=` + (this.params.sort.order > 0 ? 'asc' : 'desc'));
            }

            return params;
        },
        exportarData() {
            let params = this.loadDataParams();

            this.isLoading = true;
            Conta.download(params.join("&"))
                .then(response => {
                    File.downloadBlob(response);
                })
                .catch(error => {
                    if (error.response) {
                        if (error.response.data.msg) {
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                            return;
                        }
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        goExportacaoConsolidada(modulo) {
            this.$router.push({
                name: 'exportacao.consolidada',
                query: { modulo },
            });
        },
        loadData() {
            let params = this.loadDataParams();

            this.selectedRows = [];
            this.isLoading = true;
            Conta.list(params.join("&"))
                .then(response => {
                    this.lista = response.data.data;
                    this.pagination.perPage = response.data.per_page;
                    this.pagination.total = response.data.total;
                    this.pagination.first = response.data.per_page * this.pagination.page;
                })
                .catch(error => {
                    if (error.response) {
                        if (error.response.data.msg) {
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                            return;
                        }
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // Filter
        toggleBusca(event) {
            this.$refs.busca.toggle(event);
        },
        onClearBusca(event) {
            this.toggleBusca(event);
            this.params.filter = {};
            this.selectedClientes = [];
            this.onPage();
        },
        onSubmitBusca(event) {
            this.toggleBusca(event);
            this.onPage();
        },

        // Paginate
        onPage(event) {
            const page = event ? event.page : 0;
            this.pagination.page = page;
            this.$router.replace({ name: this.$route.name, query: { page: page } });
            this.loadData();
        },

        // Sort
        onTableSort(event) {
            this.params.sort = {
                field: event.sortField,
                order: event.sortOrder,
            };
            this.loadData();
        },

        dateFormat(value) {
            if (!value) return;
            return Moment.utc(value).format("DD/MM/YYYY");
        },

        formatMoney(valor) {
            if (!valor) return Mask.money(0);
            return Mask.money(valor);
        },

        semBoletoUrgente(data) {
            return !data.boleto_id
                && !data.data_pagamento
                && data.data_vencimento_dias !== null
                && data.data_vencimento_dias >= 0
                && data.data_vencimento_dias <= 15;
        },
    }


}
</script>
